import BaseResource from './BaseResource';

interface Content {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  pronunciation: string;
}

interface Creator {
  name: string; // TODO: verify if this is name or username
  avatar: string | null;
}

interface Company {
  title: string;
}

export type ImageGenerationStatus = 'active' | 'waiting' | 'completed' | 'failed' | 'not-found';

type GeneratedImageStatus = 'generating' | 'generated' | 'completed';

export interface EikenMaterialData {
  id: string;
  createdAt: string;
  createdBy: Creator;
  company: Company;
  image?: string;
  generatedImage?: string;
  generatedImageStatus?: GeneratedImageStatus;
  imageText: string;
  imagePrompt?: string;
  referenceText: string; // imageText
  referenceTextTitle: string; // name
  content: Content;
  imageGenerationStatus?: ImageGenerationStatus;
  isPublished: boolean;
  transitionArrowLabels?: string[];
}

export interface GeneratedEikenMaterialData {
  ids: string[];
}

type GenerateImageResponse = {
  jobId: string;
};

type GenerateImageStatusResponse = {
  status: ImageGenerationStatus;
  progress: number;
  imageUrl?: string;
};

export type AIConfig = {
  gptModelImage?: string;
  gptModelText?: string;
  masterPrompt?: string;
  imagePromptAttachments?: string[];
  imageQAPrompt?: string;
};

export type TokenCountResponse = {
  tokenCount: number;
  purchasedTokenCount: number;
};

class EikenMaterialResource extends BaseResource<EikenMaterialData> {
  static get storeName(): string {
    return 'EikenMaterialResource';
  }

  static get endpoint(): string {
    return `/${this.scope}/eiken-materials`;
  }

  static toObject(raw: any): EikenMaterialResource {
    return new EikenMaterialResource(raw as EikenMaterialData);
  }

  static async generateImage({
    id,
    prompt,
    transitionArrowLabels,
  }: {
    id: string;
    prompt: string;
    transitionArrowLabels: string[];
  }): Promise<GenerateImageResponse> {
    return await this.http.post(
      this.endpoint + '/generate-image',
      {},
      {
        id,
        imageGenerationPrompt: prompt,
        transitionArrowLabels: transitionArrowLabels,
      }
    );
  }

  static async fetchGenerateImageStatus(id: string): Promise<GenerateImageStatusResponse> {
    return await this.http.get(this.endpoint + '/generate-image/status/' + id);
  }

  static async generateRandomMaterial(count: number): Promise<GeneratedEikenMaterialData> {
    return await this.http.post(this.endpoint + '/generate', {}, { count });
  }

  static async getTokenCount(): Promise<TokenCountResponse> {
    return await this.http.get(this.endpoint + '/tokens/company-token-count').then((res) => {
      return {
        tokenCount: res.tokenCount ?? 0,
        purchasedTokenCount: res.purchasedTokenCount ?? 0,
      };
    });
  }

  static async fetchAIConfig(): Promise<AIConfig> {
    return await this.http.get(this.endpoint + '/ai/config');
  }

  static async updateAIConfig(data: AIConfig): Promise<void> {
    await this.http.post(this.endpoint + '/ai/config', {}, data);
  }

  static async getAIModels(): Promise<string[]> {
    return await this.http.get(this.endpoint + '/ai/models');
  }
}

export default EikenMaterialResource;
